import React, { useState } from 'react';

import {
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Alert } from '@mui/material';
import Box from '@mui/material/Box';
import DeleteIcon from '@mui/icons-material/Delete';

import ButtonStatus from 'components/buttons/ButtonStatus';
import HintText from 'components/labels/HintText';

import { makeStyles } from 'tss-react/mui';
import showNotification from 'commons/helpers/showNotification';
import MaskUtils from 'commons/utils/maskUtils';
import CustomerAutocompleteExceptForIdField from 'components/inputs/CustomerAutocompleteExceptForIdField';
import CustomerAutocompleteField from 'components/inputs/CustomerAutocompleteField';
import LabelStatus from 'components/labels/LabelStatus';
import ConfirmationDialog from 'components/modals/ConfirmationDialog';
import CustomerService from 'services/CustomerService';
import { FontAwesomeIconStatus } from 'components/icon/FontAwesomeIconStatus';
import { useNavigate } from 'react-router-dom';
import TableCellAction from 'components/table/TableCellAction';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  row: {
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  outerPaper: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  innerPaper: { paddingLeft: theme.spacing(2), paddingRight: theme.spacing(2) },
  innerPaperSaving: {
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  btnBack: { marginRight: 4 },
  labelHeader: { marginLeft: '8px' },
  inputSearchDefaultWidth: { width: '330px' },
  sectionContainer: {
    padding: '5px 16px',
    borderBottom: 'none',
    color: 'rgb(99, 115, 129)',
    backgroundColor: 'rgb(244, 246, 248)',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const getSuggestionLabel = (obj) => {
  if (obj) {
    if (obj.phone1) {
      return `${obj.name} - ${MaskUtils.phone(obj.phone1)}`;
    }
    return `${obj.name}`;
  }
  return '';
};

// COMPONENT
function UnifyCustomerManualPage() {
  const [selectedMainCustomer, setSelectedMainCustomer] = useState(null);
  const [selectedCustomerToUnify, setSelectedCustomerToUnify] = useState(null);
  const [customersToUnifySelection, setCustomersToUnifySelection] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
  const { classes } = useStyles();
  const navigate = useNavigate();

  const handleSubmitConfirmation = async () => {
    setIsSaving(true);
    try {
      const preferNameForFirstInTheList = true;
      const allSelectedCustomerIds = customersToUnifySelection.map((c) => c.id);
      await CustomerService.unifyCustomers(
        [[selectedMainCustomer.id, ...allSelectedCustomerIds]],
        preferNameForFirstInTheList,
      );

      showNotification('success', `Contatos unificados com sucesso.`);
      navigate('/clientes');
    } catch (e) {
      setIsModalConfirmOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const exceptForIds = React.useMemo(() => {
    let exceptList = [];
    if (selectedMainCustomer) {
      exceptList.push(selectedMainCustomer.id);
    }
    if (customersToUnifySelection) {
      exceptList = exceptList.concat(customersToUnifySelection.map((c) => c.id));
    }

    return exceptList;
  }, [selectedMainCustomer, customersToUnifySelection]);

  return (
    <>
      {/* CONFIRM */}
      <ConfirmationDialog
        title="Confirmação"
        message={
          <Alert severity="warning">
            Tem certeza que deseja unificar os contatos?
            <br />
            <b>Deseja prosseguir?</b>
          </Alert>
        }
        open={isModalConfirmOpen}
        onConfirm={handleSubmitConfirmation}
        onClose={() => {
          setIsModalConfirmOpen(false);
        }}
      />

      {/* CONFIRM */}
      <div className={classes.root}>
        <Box display="flex" direction="row" justify="center" alignItems="center">
          <IconButton onClick={() => navigate(-1)} className={classes.btnBack} size="large">
            <FontAwesomeIconStatus icon="arrow-left" status="primary" />
          </IconButton>
          <Typography variant="h5">Unificação Manual</Typography>
        </Box>

        <Paper className={classes.outerPaper}>
          {isSaving ? (
            <div className={classes.innerPaperSaving}>
              <Typography variant="subtitle1" color="textSecondary">
                Unificando cadastros, por favor aguarde...
              </Typography>
              <LinearProgress />
            </div>
          ) : (
            <>
              <div className={classes.innerPaper}>
                <LabelStatus variant="subtitle1" status="primary" className={classes.labelHeader}>
                  {selectedMainCustomer
                    ? `Cliente principal '${selectedMainCustomer.name}' selecionado`
                    : 'Selecione aqui o cliente principal'}
                </LabelStatus>
                <div className={classes.inputSearchDefaultWidth}>
                  <CustomerAutocompleteField
                    fullWidth
                    label=""
                    name="customer"
                    placeholder="Busque um cliente principal..."
                    getSuggestionLabel={getSuggestionLabel}
                    value={selectedMainCustomer}
                    onChange={(name, v) => {
                      setSelectedMainCustomer(v);
                    }}
                    disabled={isSaving}
                  />
                </div>
              </div>

              {!selectedMainCustomer ? (
                <Alert severity="info">Selecione acima o cliente principal</Alert>
              ) : (
                <>
                  <div className={classes.sectionContainer}>
                    <Typography color="textSecondary" variant="body2">
                      Lista de clientes a unificar
                    </Typography>
                  </div>
                  <div className={classes.innerPaper}>
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <div className={classes.inputSearchDefaultWidth}>
                        <CustomerAutocompleteExceptForIdField
                          fullWidth
                          label="Pesquisar cliente que será unificado:"
                          name="customer"
                          placeholder="Busque um cliente..."
                          exceptForIds={exceptForIds}
                          getSuggestionLabel={getSuggestionLabel}
                          disabled={isSaving}
                          value={selectedCustomerToUnify}
                          onChange={(name, newSelection) => {
                            setCustomersToUnifySelection(
                              (customersToUnifySelection ?? []).concat([newSelection]),
                            );
                            setSelectedCustomerToUnify(null);
                          }}
                          blurOnSelect={true}
                        />
                      </div>

                      <ButtonStatus
                        status="success"
                        variant="contained"
                        style={{ marginBottom: '10px', marginTop: '10px' }}
                        disabled={customersToUnifySelection.length === 0 || isSaving}
                        onClick={() => {
                          setIsModalConfirmOpen(true);
                        }}
                      >
                        Unificar clientes
                      </ButtonStatus>
                    </Box>
                    <Table aria-label="simple table" size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome</TableCell>
                          <TableCell>Telefone 1</TableCell>
                          <TableCell>Telefone 2</TableCell>
                          <TableCell>Telefone Internacional</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {customersToUnifySelection.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <HintText>
                                Nenhum adicionado ainda. Pesquise no campo acima e adione para unificar.
                              </HintText>
                            </TableCell>
                          </TableRow>
                        )}
                        {customersToUnifySelection?.map((customer, currentIndex) => {
                          return (
                            <TableRow key={customer.id}>
                              <TableCell component="th" scope="row">
                                {customer.name}
                              </TableCell>
                              <TableCell component="th" scope="row">
                                {MaskUtils.phone(customer.phone1)}
                              </TableCell>
                              <TableCell component="th" scope="row">
                                {MaskUtils.phone(customer.phone2)}
                              </TableCell>
                              <TableCell component="th" scope="row">
                                {customer.phoneInternational}
                              </TableCell>
                              <TableCellAction
                                title="Deletar"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setCustomersToUnifySelection((prev) =>
                                    prev.filter((o, i) => i !== currentIndex),
                                  );
                                }}
                              >
                                <DeleteIcon color="error" />
                              </TableCellAction>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </Paper>
      </div>
    </>
  );
}

export default UnifyCustomerManualPage;
